/**
 * [T149-PR3] CashflowComposedSkin — `cashflow-composed` adapter.
 *
 * Renders a stacked income/expense bar with a net-flow line overlay.
 * Mirrors the legacy `CashFlowChart.tsx` visual contract while consuming
 * the canonical `points: [{date, income, expense, net}]` resolver shape.
 */
import { BadgeDollarSign } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { INSIGHT_CHART_MARGIN } from '@/lib/chartLayout';
import { useCurrency } from '@/hooks/useCurrency';
import { useWindowSize } from '@/hooks/useWindowSize';
import { WidgetFrame } from '../WidgetFrame';
import { RechartsCell } from '../primitives/RechartsCell';
import type { SkinProps } from './types';
import { skinTestId, SkinLoadingLine, SkinLoadingRows, SkinEmptyState, SkinErrorState, useSkinFrameProps } from './_shared';

const MIN_W = 3;
const MIN_H = 2;

const chartConfig: ChartConfig = {
  income: { label: 'Income', color: 'hsl(var(--finance-positive))' },
  expense: { label: 'Expense', color: 'hsl(var(--finance-negative))' },
  net: { label: 'Net', color: 'hsl(var(--chart-3))' },
};

export default function CashflowComposedSkin(props: SkinProps<'cashflow-composed'>) {
  const { widget, data, loading, error } = props;
  const points = data?.points ?? [];
  const { fmt } = useCurrency();
  const { width } = useWindowSize();
  const isMobile = (width ?? 1280) < 480;
  const isTooSmall = widget.w < MIN_W || widget.h < MIN_H;
  const frameProps = useSkinFrameProps(props);

  // Net total for period
  const netTotal = points.reduce((sum, p) => sum + p.net, 0);

  // Too-small fallback: net total KPI
  if (isTooSmall) {
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Cash Flow'}
        icon={<BadgeDollarSign className="h-4 w-4" />}
        {...frameProps}
        body={
          <div className="flex flex-col justify-center h-full gap-1 py-1">
            {loading ? (
              <SkinLoadingRows rows={2} />
            ) : points.length > 0 ? (
              <>
                <p className="text-xs text-muted-foreground">Net period</p>
                <p
                  className={`text-lg font-bold truncate ${netTotal >= 0 ? 'text-green-600' : 'text-destructive'}`}
                >
                  {netTotal >= 0 ? '+' : ''}{fmt(netTotal)}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}
          </div>
        }
      />
    );
  }

  // Mobile fallback: compact list of last 7 data points (income vs expense)
  if (isMobile) {
    const last7 = points.slice(-7);
    return (
      <WidgetFrame
        id={widget.id}
        title={widget.title || 'Cash Flow'}
        icon={<BadgeDollarSign className="h-4 w-4" />}
        {...frameProps}
        body={
          loading ? (
            <SkinLoadingRows rows={7} />
          ) : error ? (
            <SkinErrorState message={typeof error === 'string' ? error : (error as unknown as Error)?.message} onRetry={props.onRetry} />
          ) : points.length === 0 ? (
            <div data-testid={skinTestId('cashflow-composed', widget.id, 'empty')}>
              <SkinEmptyState
                title="No cashflow data"
                description="Once you record transactions, daily cashflow will appear here."
                cta={{ label: 'Add transaction', to: '/transactions' }}
              />
            </div>
          ) : (
            <ul className="space-y-0.5 w-full" role="list" aria-label="Cash flow by day">
              {last7.map((p) => (
                <li key={p.date} className="flex items-center gap-2 min-w-0 py-1 text-xs">
                  <span className="w-20 shrink-0 text-muted-foreground">{p.date.slice(5)}</span>
                  <span className="flex-1 text-green-600">{fmt(p.income)}</span>
                  <span className="shrink-0 text-destructive">{fmt(p.expense)}</span>
                </li>
              ))}
            </ul>
          )
        }
      />
    );
  }

  // Main render
  return (
    <WidgetFrame
      id={widget.id}
      title={widget.title || 'Cash Flow'}
      icon={<BadgeDollarSign className="h-4 w-4" />}
      {...frameProps}
      body={
        loading ? (
          <div data-testid={skinTestId('cashflow-composed', widget.id, 'loading')}>
            <SkinLoadingLine />
          </div>
        ) : error ? (
          <div data-testid={skinTestId('cashflow-composed', widget.id, 'error')}>
            <SkinErrorState message={error.message} onRetry={props.onRetry} />
          </div>
        ) : points.length === 0 ? (
          <div data-testid={skinTestId('cashflow-composed', widget.id, 'empty')}>
            <SkinEmptyState
              title="No cashflow data"
              description="Once you record transactions, daily cashflow will appear here."
              cta={{ label: 'Add transaction', to: '/transactions' }}
            />
          </div>
        ) : (
          <RechartsCell minHeight={250}>
            <ChartContainer
              config={chartConfig}
              className="h-[250px] md:h-[320px] w-full"
            >
              <ComposedChart data={points} margin={INSIGHT_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v: string) => {
                    try {
                      return new Date(v + '-01').toLocaleDateString(undefined, { month: 'short' });
                    } catch {
                      return v;
                    }
                  }}
                />
                <YAxis
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip content={<ChartTooltipContent labelKey="date" />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar
                  dataKey="income"
                  stackId="cash"
                  radius={[6, 6, 0, 0]}
                  fill="var(--color-income)"
                  maxBarSize={36}
                />
                <Bar
                  dataKey="expense"
                  stackId="cash"
                  radius={[6, 6, 0, 0]}
                  fill="var(--color-expense)"
                  maxBarSize={36}
                />
                <Line
                  type="monotone"
                  dataKey="net"
                  stroke="var(--color-net)"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: 'var(--color-net)' }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ChartContainer>
          </RechartsCell>
        )
      }
    />
  );
}
